import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Check } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Eyebrow } from "../components/Reveal";
import { PrimaryButton } from "../components/Buttons";
import { MODULES } from "../data/modules";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: MODULES.map((m, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "SoftwareApplication",
      name: `Velacin — ${m.title}`,
      description: m.description,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
    },
  })),
};

function ModuleRow({ m, index }) {
  const reversed = index % 2 === 1;
  const Icon = m.icon;
  return (
    <article
      id={m.slug}
      data-testid={`module-${m.slug}`}
      className="scroll-mt-28 border-t border-white/10 py-16 md:py-24"
    >
      <div
        className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${
          reversed ? "lg:[&>*:first-child]:order-2" : ""
        }`}
      >
        {/* Text */}
        <Reveal>
          <div>
            <div className="flex items-center gap-4">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#4C6EF5]/15 text-[#8FA4FF]">
                <Icon className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <span className="font-mono-label text-xs uppercase tracking-widest text-white/40">
                {m.index} / 20
              </span>
            </div>
            <h2 className="mt-6 text-2xl font-bold tracking-tight text-white sm:text-3xl">
              {m.title}
            </h2>
            <p className="mt-2 text-base font-medium text-[#8FA4FF]">
              {m.tagline}
            </p>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/60 md:text-base">
              {m.description}
            </p>
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {m.highlights.map((h) => (
                <div key={h} className="flex items-center gap-2.5">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#4C6EF5]/20 text-[#8FA4FF]">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="text-sm text-white/75">{h}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Visual panel */}
        <Reveal delay={0.1}>
          <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A0A]">
            <div className="absolute inset-0 grid-overlay opacity-60" />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 45%, rgba(76,110,245,0.28) 0%, rgba(0,0,0,0) 70%)",
              }}
            />
            <div className="absolute inset-0 grid place-items-center">
              <Icon
                className="h-28 w-28 text-white/90 sm:h-32 sm:w-32"
                strokeWidth={0.75}
              />
            </div>
            <span className="absolute bottom-4 left-4 font-mono-label text-xs uppercase tracking-widest text-white/40">
              {m.slug}
            </span>
          </div>
        </Reveal>
      </div>
    </article>
  );
}

export default function Features() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [hash]);

  return (
    <>
      <Seo
        title="Features & Modules — Velacin"
        description="Every Velacin module in one place: attendance, leave, projects, kanban, sprints, tasks, timesheets, expenses, onboarding, offboarding, recruitment, referrals, issues, assets, gamification, insights, announcements, idle tracker, custom roles, budgets, and branding."
        path="/features"
        jsonLd={jsonLd}
      />

      <section className="bg-black pt-36 pb-8 md:pt-44">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Modules</Eyebrow>
            <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Everything Velacin does.
            </h1>
            <p className="mt-6 max-w-2xl text-base text-white/60 md:text-lg">
              Twenty first-class modules that share data, permissions, and
              design language. Every module below is production-ready and
              available to every workspace.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-black">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {MODULES.map((m, i) => (
            <ModuleRow key={m.slug} m={m} index={i} />
          ))}
        </div>
      </section>

      <section className="bg-[#0A0A0A] py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <Reveal>
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              See it in your workspace.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base text-white/65 md:text-lg">
              Book a walkthrough and we'll show you the modules that matter to
              your team, live.
            </p>
            <div className="mt-9 flex justify-center">
              <PrimaryButton to="/contact" testId="features-book-demo">
                Book a demo
              </PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
